import { computed, ref } from "vue";
import type { PartnerActivityRow } from "@/types";

/**
 * Shared "click an activity → confirm switching into that client's org" behaviour, used by both
 * the partner dashboard widget (`PartnerActivity.vue`) and the full Partner Activity page
 * (`PartnerActivityView.vue`).
 *
 * Owns the modal open-state + selected row, and derives the `OrganizationSwitchModal` props so the
 * two call sites bind a single `v-bind="orgSwitch.modalProps"` instead of duplicating the wiring.
 * Call `open(activity)` from a row click; spread `modalProps` onto `<OrganizationSwitchModal>` and
 * forward its events to `onShowChange` / `onCancelled`.
 */
export function useActivityOrgSwitch() {
  const show = ref(false);
  const selected = ref<PartnerActivityRow | null>(null);

  function open(activity: PartnerActivityRow) {
    selected.value = activity;
    show.value = true;
  }

  function onShowChange(value: boolean) {
    show.value = value;
  }

  function onCancelled() {
    selected.value = null;
  }

  const modalProps = computed(() => ({
    show: show.value,
    targetOrganization: {
      id: selected.value?.clientOrgId ?? "",
      name: selected.value?.clientOrgName ?? "",
    },
    targetRoute: selected.value?.projectId
      ? { name: "project-overview", params: { projectid: selected.value.projectId } }
      : { name: "home" },
    context: "activity" as const,
  }));

  return { show, selected, open, onShowChange, onCancelled, modalProps };
}
